import { Emitter } from '@patsnap/tahiti-core';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FtoReportContext } from '../novelty-check-report-context';
import type { FtoReportState } from '../novelty-check-report-state';
import { FtoReportInitialState } from './initial-state';
import { FtoReportTechFeaturePullSuccessState } from './tech-feature-pull-success-state';
import { FtoReportSearchFinishedState } from './search-finished-state';
import { FtoReportResultState } from './result-state';
import { FtoTaskStep } from '@/domains/fto-report/enums';
import { FtoReportTaskStatusService } from '../../task-status-service';
import { FtoReportSearchInputState } from './search-input-state';
import { FtoReportTechFeaturePullStartState } from './tech-feature-pull-start-state';
import { FtoReportSummaryStartState } from './summary-start-state';
import { FtoReportSummaryEndState } from './summary-end-state';

@injectable()
export class FtoReportContextImpl implements FtoReportContext {
    private _currentState!: FtoReportState;
    private _previousState!: FtoReportState | null;
    private _states: Map<FtoTaskStep, FtoReportState> = new Map();
    private _stateChangeEmitter = new Emitter<FtoTaskStep>();

    readonly onStateChange = this._stateChangeEmitter.event;

    // eslint-disable-next-line max-params
    constructor(
        @inject(FtoReportTaskStatusService)
        private readonly _noveltyCheckTaskStatusService: FtoReportTaskStatusService,
        @inject(FtoReportInitialState) initialState: FtoReportInitialState,
        @inject(FtoReportSummaryStartState) summaryState: FtoReportSummaryStartState,
        @inject(FtoReportSummaryEndState) summaryEndState: FtoReportSummaryEndState,
        @inject(FtoReportSearchInputState) searchInputState: FtoReportSearchInputState,
        @inject(FtoReportTechFeaturePullStartState) techFeatureIdentifyStartState: FtoReportTechFeaturePullStartState,
        @inject(FtoReportTechFeaturePullSuccessState) techFeatureIdentifyEndState: FtoReportTechFeaturePullSuccessState,
        @inject(FtoReportSearchFinishedState) confirmState: FtoReportSearchFinishedState,
        @inject(FtoReportResultState) resultState: FtoReportResultState,
    ) {
        // 初始化所有状态
        this._states.set(FtoTaskStep.INITIAL, initialState);
        this._states.set(FtoTaskStep.SUMMARY_START, summaryState);
        this._states.set(FtoTaskStep.SUMMARY_END, summaryEndState);
        this._states.set(FtoTaskStep.FTO_SEARCH_INPUT, searchInputState);
        this._states.set(FtoTaskStep.TECH_FEATURE_PULL_START, techFeatureIdentifyStartState);
        this._states.set(FtoTaskStep.TECH_FEATURE_PULL_SUCCESS, techFeatureIdentifyEndState);
        this._states.set(FtoTaskStep.SEARCH_END, confirmState);
        this._states.set(FtoTaskStep.REPORT_CREATED, resultState);
    }

    getCurrentState(): FtoReportState {
        return this._currentState;
    }

    getPreviousState(): FtoReportState | null {
        return this._previousState;
    }

    getCurrentStep(): FtoTaskStep {
        return this._currentState?.getStep();
    }

    getState(step: FtoTaskStep): FtoReportState {
        const state = this._states.get(step);
        if (!state) {
            throw new Error(`未找到状态: ${step}`);
        }
        return state;
    }

    async transitionTo(state: FtoReportState): Promise<void> {
        if (this._currentState === state) {
            return;
        }

        // 执行当前状态的退出逻辑
        await this._currentState?.exit(this);

        this._previousState = this._currentState;

        // 切换状态
        this._currentState = state;

        // 执行新状态的进入逻辑
        await this._currentState.enter(this, () => {
            return this._noveltyCheckTaskStatusService.pullTaskStatus();
        });

        // 触发状态变化事件
        this._stateChangeEmitter.fire(this.getCurrentStep());
    }

    async next(): Promise<void> {
        await this._currentState.handleNext(this);
    }

    async previous(): Promise<void> {
        await this._currentState.handlePrevious(this);
    }

    async initialize(): Promise<void> {
        // 获取任务状态
        const taskStatus = await this._noveltyCheckTaskStatusService.pullTaskStatus();
        const currentState = this.getState(taskStatus.taskStep);
        this.transitionTo(currentState);
    }
}
